import { ActivityIndicator, Text as RNText, View } from 'react-native';

import { cn } from './cn';

export type BannerVariant = 'positive' | 'caution' | 'alert' | 'neutral';
export type BannerIcon = 'none' | 'lock' | 'dot' | 'check' | 'mail';

const CONTAINER: Record<BannerVariant, string> = {
  positive: 'bg-positive-bg border-positive-border',
  caution: 'bg-caution-bg border-caution-border',
  alert: 'bg-alert-bg border-alert-border',
  neutral: 'bg-card border-border',
};

const TITLE: Record<BannerVariant, string> = {
  positive: 'text-positive',
  caution: 'text-caution',
  alert: 'text-alert',
  neutral: 'text-foreground-secondary',
};

const DOT: Record<BannerVariant, string> = {
  positive: 'bg-positive',
  caution: 'bg-caution',
  alert: 'bg-alert',
  neutral: 'bg-muted',
};

const SPINNER: Record<BannerVariant, string> = {
  positive: 'accent-positive',
  caution: 'accent-caution',
  alert: 'accent-alert',
  neutral: 'accent-muted',
};

export interface StatusBannerProps {
  variant?: BannerVariant;
  icon?: BannerIcon;
  title: string;
  subtitle?: string;
  showSpinner?: boolean;
  className?: string;
}

/**
 * The offline / queued / sealed reassurance banner from the design.
 * Used standalone or wrapped by SyncStatusBanner (live PowerSync status).
 */
export function StatusBanner({
  variant = 'caution',
  icon = 'none',
  title,
  subtitle,
  showSpinner,
  className,
}: StatusBannerProps) {
  return (
    <View
      className={cn(
        'flex-row items-center gap-3 rounded-xl border-[1.5px] px-3.5 py-3',
        CONTAINER[variant],
        className,
      )}
    >
      {icon === 'lock' ? <RNText className="text-[15px]">🔒</RNText> : null}
      {icon === 'mail' ? <RNText className="text-[15px]">📨</RNText> : null}
      {icon === 'dot' ? (
        <View className={cn('h-2.5 w-2.5 rounded-full', DOT[variant])} />
      ) : null}
      {icon === 'check' ? (
        <View
          className={cn('h-5 w-5 items-center justify-center rounded-full', DOT[variant])}
        >
          <View
            className="h-[5px] w-2 border-b-2 border-l-2 border-white"
            style={{ transform: [{ rotate: '-45deg' }, { translateY: -1 }] }}
          />
        </View>
      ) : null}

      <View className="flex-1">
        <RNText className={cn('font-sans-bold text-[13.5px]', TITLE[variant])}>
          {title}
        </RNText>
        {subtitle ? (
          <RNText className="font-sans text-xs text-foreground-secondary">
            {subtitle}
          </RNText>
        ) : null}
      </View>

      {showSpinner ? (
        <ActivityIndicator size="small" colorClassName={SPINNER[variant]} />
      ) : null}
    </View>
  );
}
